import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import ChatBotHeader from "./components/header";
import BotChatWindow from "./components/ChatWindow";

/**
 * DockableChatbot
 * - Docked mode: a right-hand sidebar panel (default).
 * - Expanded mode: full-screen overlay.
 * - Toggle with the arrow button in the header.
 *
 * Requirements:
 * - Tailwind CSS
 * - framer-motion
 * - lucide-react
 *
 * Usage:
 * <DockableChatbot />
 *
 * Notes:
 * - Esc closes (returns to docked) when expanded.
 * - Focus stays within input when sending messages.
 */

export default function DockableChatbot({ onClose }: { onClose: () => void }) {
  const [expanded, setExpanded] = useState(false);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [isMobile, setIsMobile] = useState(false);

  // screen check
  useEffect(() => {
    const checkScreen = () => setIsMobile(window.innerWidth < 768);
    checkScreen();
    window.addEventListener("resize", checkScreen);
    return () => window.removeEventListener("resize", checkScreen);
  }, []);

  // close on ESC
  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: "smooth",
    });

    const onKey = (e: KeyboardEvent) => {
      if (expanded && e.key === "Escape") setExpanded(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [expanded]);

  return (
    <>
      <div className="fixed h-full w-full top-0 left-0 z-10 bg-black bg-opacity-50"></div>

      {/* Docked Panel (always rendered, but hidden when expanded for simplicity) */}
      <AnimatePresence>
        {!expanded && (
          <motion.aside
            key="docked"
            initial={{ x: 400, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: 400, opacity: 0 }}
            transition={{ type: "spring", stiffness: 260, damping: 28 }}
            className={`fixed right-0 top-1/3 transform -translate-y-1/2 z-40 h-2/3 max-w-sm bg-darkCard shadow-2xl rounded-xl
                        ${isMobile ? "w-[90%]" : "w-1/2"}`}
            aria-label="Chatbot docked panel"
          >
            <ChatBotHeader
              expanded={false}
              onExpand={() => setExpanded(true)}
              onClose={onClose}
            />
            <BotChatWindow
              scrollRef={scrollRef}
              inputRef={inputRef}
              expanded={expanded}
            />
          </motion.aside>
        )}
      </AnimatePresence>

      {/* Expanded Fullscreen Overlay */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            key="expanded"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-hrmCardDark "
            aria-modal
            role="dialog"
            aria-label="Chatbot expanded"
          >
            <div className="flex h-full w-full flex-col border ">
              <ChatBotHeader
                expanded
                onExpand={() => setExpanded(false)}
                onClose={onClose}
              />
              <BotChatWindow
                scrollRef={scrollRef}
                inputRef={inputRef}
                expanded={expanded}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
